import React, { useRef } from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { margins } from "style";
import Form, { SIZE, TYPE } from "components/Form";
import { MUTATION_TYPE } from "hooks";
import { UPDATE_APPLICANT } from "graphql/mutations";
import { convertFromPlainText, getPlainText } from "components/Form/Editor";
import { AiSummaryDisplay } from "components/AiSummary";
import Card from "components/Card";
import { H3 } from "components/Text";
import { Message } from "components/DisplayBox";

/**
 * ApplicationNotesForm
 *
 * @param {Object}  data
 * @param {String}  fieldName
 * @param {String}  summaryFieldName
 * @param {Boolean} loading
 * @param {Boolean} readOnly
 */
const ApplicationNotesForm = ({ data, fieldName, summaryFieldName, loading, readOnly, ...props }) => {
  const jsonFieldName = `${fieldName}Json`;
  const form = useRef(null);
  const handleSave = () => form.current.triggerSubmitForm();

  return (
    <>
      {summaryFieldName && (
        <Card css={styles.aiSummaryCard}>
          <H3>
            <FormattedMessage id="NotesSummary.AI.Title" />
          </H3>
          {data?.missionData?.[summaryFieldName] ? (
            <AiSummaryDisplay
              summary={data?.missionData?.[summaryFieldName]}
              loading={loading}
              showRegenerateButton={false}
              showRatings={false}
            />
          ) : (
            <Message>
              <FormattedMessage id="AISummary.NoSummary" />
            </Message>
          )}
        </Card>
      )}
      <Form
        ref={form}
        data={FORM_DATA(handleSave, fieldName, { readOnly })}
        initialValues={{
          [jsonFieldName]: data?.[jsonFieldName] ?? convertFromPlainText(data?.[jsonFieldName]),
        }}
        mutationType={MUTATION_TYPE.update}
        mutationData={{
          mutationName: { update: UPDATE_APPLICANT },
        }}
        loading={loading}
        marginSize={margins.none}
        updateId={data?.id}
        noPadding
        {...props}
      />
    </>
  );
};

const FORM_DATA = (onSave, fieldName, props) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        type: TYPE.editor,
        size: SIZE.xlarge,
        onChange: (name, value, handleOnChange) => {
          handleOnChange(fieldName, getPlainText(value));
          handleOnChange(name, value);
        },
        properties: {
          name: `${fieldName}Json`,
          placeholder: "Add notes",
          showFormattingOptions: true,
          onBlur: onSave,
          ...props,
        },
      },
    ],
  },
];

const styles = {
  aiSummaryCard: css`
    margin-bottom: ${margins.normal};
    display: flex;
    flex-direction: column;
    gap: ${margins.xxsmall};
  `,
};

ApplicationNotesForm.propTypes = {
  data: PropTypes.object.isRequired,
  fieldName: PropTypes.string.isRequired,
  summaryFieldName: PropTypes.string,
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default ApplicationNotesForm;
